import React, { useState } from 'react';

const OptimizedImage = ({ 
  src, 
  alt, 
  width = 400, 
  height = 300, 
  className = '', 
  style = {},
  fit = 'crop',
  quality = 80,
  placeholder = 'https://images.unsplash.com/photo-1560472355-536de3962603?w=400&h=300&fit=crop'
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Function to optimize image URL
  const getOptimizedImageUrl = (originalUrl) => {
    if (!originalUrl || typeof originalUrl !== 'string' || originalUrl === '') return placeholder;
    
    // Check for invalid URLs that would cause ERR_NAME_NOT_RESOLVED
    try {
      new URL(originalUrl);
    } catch (error) {
      console.warn('Invalid URL detected:', originalUrl, 'Using placeholder instead');
      return placeholder;
    }
    
    // If it's already a blob URL (from file upload), use it directly
    if (originalUrl.startsWith('blob:')) {
      return originalUrl;
    }
    
    // Check if it's a Cloudinary URL
    if (originalUrl.includes('cloudinary.com')) {
      // For Cloudinary URLs, we can add transformation parameters
      // Example: https://res.cloudinary.com/dgban38oz/image/upload/v1234/folder/image.jpg
      // Becomes: https://res.cloudinary.com/dgban38oz/image/upload/w_400,h_300,c_fill,q_auto/v1234/folder/image.jpg
      
      const parts = originalUrl.split('/upload/');
      if (parts.length === 2) {
        const transformations = `w_${width},h_${height},c_fill,q_auto,f_auto`;
        return `${parts[0]}/upload/${transformations}/${parts[1]}`;
      }
      return originalUrl; // Fallback to original if parsing fails
    }
    
    // Check if it's an Unsplash URL
    if (originalUrl.includes('unsplash.com')) {
      // Extract the base URL without existing parameters
      const baseUrl = originalUrl.split('?')[0];
      // Add our optimization parameters
      return `${baseUrl}?w=${width}&h=${height}&fit=${fit}&q=${quality}&auto=format`;
    }
    
    // For other URLs that already have parameters, use them as-is to avoid conflicts
    if (originalUrl.includes('w=') && originalUrl.includes('h=')) {
      return originalUrl;
    }
    
    // For other URLs, try to add parameters if they support it
    if (originalUrl.includes('?')) {
      return `${originalUrl}&w=${width}&h=${height}&fit=${fit}`;
    } else {
      return `${originalUrl}?w=${width}&h=${height}&fit=${fit}`;
    }
  };

  const optimizedSrc = getOptimizedImageUrl(src);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const imageStyle = {
    ...style,
    width: style.width || '100%',
    height: style.height || '100%',
    objectFit: 'cover',
    transition: 'opacity 0.3s ease-in-out',
    opacity: isLoading ? 0 : 1,
  };

  const placeholderStyle = {
    ...imageStyle,
    opacity: isLoading || hasError ? 1 : 0,
    position: isLoading || hasError ? 'static' : 'absolute',
    backgroundColor: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280',
    fontSize: '14px',
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Placeholder/Loading state */}
      {(isLoading || hasError) && (
        <div style={placeholderStyle}>
          {hasError ? 'Failed to load' : 'Loading...'}
        </div>
      )}
      
      {/* Actual image */}
      <img
        src={hasError ? placeholder : optimizedSrc}
        alt={alt}
        style={imageStyle}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
      />
    </div>
  );
};

export default OptimizedImage;